<template>
    <q-expansion-item
        class="form-definitions-editor"
        icon="settings"
        label="Manage referral form definitions"
        header-class="text-primary"
        @show="loadDefinitions"
    >
        <div class="form-definitions-content">
            <q-select
                v-model="selectedFormType"
                :options="definitionOptions"
                label="Form definition"
                outlined
                dense
                emit-value
                map-options
                :loading="loading"
            />

            <template v-if="selectedDefinition">
                <q-input
                    v-model="name"
                    class="q-mt-md"
                    label="Display name"
                    outlined
                    dense
                />
                <q-input
                    v-model="fieldDefinitionJson"
                    class="q-mt-md"
                    label="Referral-specific fields (JSON)"
                    hint='Add only referral-specific fields as keys and extraction instructions, for example {"prison":"The prison named in the referral"}.'
                    type="textarea"
                    outlined
                    autogrow
                />
                <q-banner class="bg-blue-1 text-blue-10 q-mt-sm" rounded dense>
                    <strong>Included automatically for every referral type:</strong>
                    person name, preferred name, date of birth, gender, ethnicity, iwi, NHI,
                    contact details and address; plus service, referral source, risk factor and
                    entry date for the Client Engagement. Do not repeat these fields below.
                </q-banner>
                <q-toggle v-model="isActive" class="q-mt-sm" label="Active" />

                <div class="row justify-end q-mt-md">
                    <q-btn
                        unelevated
                        no-caps
                        color="primary"
                        label="Save definition"
                        :loading="saving"
                        @click="saveDefinition"
                    />
                </div>
            </template>
        </div>
    </q-expansion-item>
</template>

<script>
import api from "@/services/api/api";
import notify from "@/services/util/notify";

export default {
    name: "DocumentFormDefinitionsEditor",
    data() {
        return {
            definitions: [],
            selectedFormType: null,
            name: "",
            fieldDefinitionJson: "{}",
            isActive: true,
            loading: false,
            saving: false,
        };
    },
    computed: {
        definitionOptions() {
            return this.definitions.map(definition => ({
                label: `${definition.name} (${definition.formType})`,
                value: definition.formType,
            }));
        },
        selectedDefinition() {
            return this.definitions.find(definition => definition.formType === this.selectedFormType) || null;
        },
    },
    watch: {
        selectedFormType() {
            this.populateEditor();
        },
    },
    methods: {
        populateEditor() {
            if (!this.selectedDefinition) return;
            this.name = this.selectedDefinition.name;
            this.fieldDefinitionJson = JSON.stringify(this.selectedDefinition.fieldDefinition, null, 2);
            this.isActive = this.selectedDefinition.isActive;
        },
        async loadDefinitions() {
            if (this.loading) return;

            this.loading = true;
            try {
                const response = await api.documentScraping.listFormDefinitions();
                this.definitions = response.data;
                this.selectedFormType = this.selectedFormType || this.definitions[0]?.formType || null;
                this.populateEditor();
            } catch (error) {
                notify.error("Failed to load referral form definitions", "top");
            } finally {
                this.loading = false;
            }
        },
        parseFieldDefinition() {
            let fieldDefinition;
            try {
                fieldDefinition = JSON.parse(this.fieldDefinitionJson);
            } catch (error) {
                throw new Error(`Field definition is not valid JSON: ${error.message}`);
            }
            if (!fieldDefinition || typeof fieldDefinition !== "object" || Array.isArray(fieldDefinition)) {
                throw new Error("Field definition must be a JSON object");
            }
            return fieldDefinition;
        },
        async saveDefinition() {
            if (!this.selectedFormType) return;

            let fieldDefinition;
            try {
                fieldDefinition = this.parseFieldDefinition();
            } catch (error) {
                notify.error(error.message, "top");
                return;
            }

            this.saving = true;
            try {
                const response = await api.documentScraping.updateFormDefinition(this.selectedFormType, {
                    name: this.name,
                    fieldDefinition,
                    isActive: this.isActive,
                });
                this.definitions = this.definitions.map(definition => (
                    definition.formType === response.data.formType ? response.data : definition
                ));
                this.populateEditor();
                notify.primary("Referral form definition updated", "top");
            } catch (error) {
                const responseMessage = typeof error?.response?.data === "string" ? error.response.data : null;
                notify.error(responseMessage || "Failed to update referral form definition", "top");
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.form-definitions-editor {
    margin-bottom: 16px;
    border: 1px solid rgba(47, 62, 70, 0.14);
    border-radius: 4px;
}

.form-definitions-content {
    padding: 8px 16px 20px;
}
</style>
